import {
  DEFAULT_STOREFRONT_TEMPLATE_ID,
  parsePageTemplates,
  resolveStorefrontPageTemplates,
  STOREFRONT_TEMPLATE_IDS,
  type StorefrontTemplateId,
  storefrontTemplateIdSchema,
} from "@loot-os/api-contract";
import {
  pageTemplatesFor,
  type StorefrontPageTemplates,
} from "@loot-os/design-system/templates/registry";

import type { TenantConfig } from "../lib/tenant.js";

/**
 * A storefront template: every page the storefront renders through one, each a design-system
 * page template. Content is resolved once per page, in `pages/`, and is the same under every
 * template; each page's parity suite (`pages/<page>/parity.test.tsx`) runs against every layout
 * in that page's catalog (`pageTemplateCases`).
 */
export type StorefrontTemplate = StorefrontPageTemplates;

/**
 * Every storefront template the SPA can render, by the ids api-contract lists, with no per-page
 * picks. Which page template each page uses is `STOREFRONT_TEMPLATE_PAGES` in api-contract and
 * the design system's catalogs hold the templates, so adding one is an id there and its pages'
 * ids.
 */
export const STOREFRONT_TEMPLATES = Object.fromEntries(
  STOREFRONT_TEMPLATE_IDS.map((id) => [id, pageTemplatesFor(resolveStorefrontPageTemplates(id))]),
) as Record<StorefrontTemplateId, StorefrontTemplate>;

/**
 * The environment's templates: its storefront template (the default for a config cached before
 * the field existed, or under an id since retired) with its per-page picks laid over it, resolved
 * the way the dashboard's theme preview resolves them, so the preview draws the same layouts. A
 * pick a page no longer offers is dropped, as is one cached before picks existed.
 */
export function storefrontTemplateOf(config: TenantConfig): StorefrontTemplate {
  const cached = config as Partial<TenantConfig>;
  const id = storefrontTemplateIdSchema.safeParse(cached.template);
  const template = id.success ? id.data : DEFAULT_STOREFRONT_TEMPLATE_ID;
  const picks = parsePageTemplates(cached.pageTemplates);
  if (Object.keys(picks).length === 0) return STOREFRONT_TEMPLATES[template];
  return pageTemplatesFor(resolveStorefrontPageTemplates(template, picks));
}
